import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { WhatsAppIcon } from "@/components/icons/WhatsAppIcon";
import bannerGeneral from "@/assets/banner_general.jpg";
import bannerHalloween from "@/assets/banner_halloween.jpg";

const WHATSAPP_CONTACTS = [
  { number: "573024684782", labelKey: "hero.bogotaCundinamarca" },
  { number: "573173163378", labelKey: "hero.restOfCountry" },
] as const;

const SLIDE_INTERVAL_MS = 6000;

type HeroSlide = {
  image: string;
  alt: string;
  buttonsPosition: string;
  buttonColor: string;
};

const slides: HeroSlide[] = [
  {
    image: bannerGeneral,
    alt: "Famibelleza",
    buttonsPosition: "bottom-[20px] left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px]",
    buttonColor: "bg-brand text-white",
  },
  {
    image: bannerHalloween,
    alt: "Famibelleza Halloween",
    buttonsPosition: "bottom-[80px] left-1/2 -translate-x-1/2 w-max",
    buttonColor: "bg-[#f47d0c] text-black",
  },
];

export function Hero() {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (isHovered) return;
    const id = setInterval(() => {
      setActiveIndex(i => (i + 1) % slides.length);
    }, SLIDE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isHovered, activeIndex]);

  const goToPrevSlide = () => setActiveIndex(i => (i - 1 + slides.length) % slides.length);
  const goToNextSlide = () => setActiveIndex(i => (i + 1) % slides.length);

  return (
    <div
      className="hero relative min-h-[40vh] w-full rounded-3xl overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {slides.map((slide, i) => (
        <div
          key={slide.image}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === activeIndex ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={slide.image}
            alt={slide.alt}
            className="h-full w-full object-cover"
            draggable={false}
          />
          <div className={`absolute bottom-3 flex flex-col gap-4 w-[300px] left-1/2 -translate-x-1/2`}>
            {WHATSAPP_CONTACTS.map(contact => (
              <a
                key={contact.number}
                href={`https://wa.me/${contact.number}`}
                target="_blank"
                rel="noopener noreferrer"
                className={`${slide.buttonColor} flex w-auto items-center justify-center gap-2 whitespace-nowrap rounded-full px-5 py-2.5 font-semibold shadow-md hover:brightness-110 transition-all duration-300`}
              >
                <WhatsAppIcon className="size-5" />
                {t(contact.labelKey)}
              </a>
            ))}
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={goToPrevSlide}
        aria-label={t("hero.previousSlide")}
        className="absolute top-1/2 left-3 -translate-y-1/2 z-10 flex items-center justify-center size-9 rounded-full bg-white/30 text-white backdrop-blur-md hover:bg-white/50 transition-colors duration-300 cursor-pointer"
      >
        <ChevronLeft className="size-6" />
      </button>
      <button
        type="button"
        onClick={goToNextSlide}
        aria-label={t("hero.nextSlide")}
        className="absolute top-1/2 right-3 -translate-y-1/2 z-10 flex items-center justify-center size-9 rounded-full bg-white/30 text-white backdrop-blur-md hover:bg-white/50 transition-colors duration-300 cursor-pointer"
      >
        <ChevronRight className="size-6" />
      </button>
    </div>
  );
}
